import React from "react";
import styled from "styled-components";
import {
  greyText,
  lightGreyOutline,
  votingAlignmentBarsPalette,
} from "../../../styles";
import { VotingAlignment } from "../../../models/stewardshipModels";

const LABEL_COLUMN_WIDTH = "220px";
const PERCENTAGE_TICKS = Array.from({ length: 11 }, (_, index) => index * 10);

const ChartContainer = styled.div`
  width: 90%;
  margin-top: 18px;
`;

const BarsViewport = styled.div`
  max-height: 248px;
  padding: 4px 12px 4px 0;
  overflow-y: auto;
  scrollbar-gutter: stable;
`;

const AlignmentRow = styled.div`
  display: grid;
  grid-template-columns: ${LABEL_COLUMN_WIDTH} minmax(0, 1fr);
  align-items: center;
  column-gap: 20px;
  height: 28px;
`;

const ManagerLabel = styled.span`
  overflow: hidden;
  color: ${greyText};
  font-size: 12px;
  line-height: 16px;
  text-align: right;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const AlignmentTrack = styled.div`
  display: flex;
  width: 100%;
  height: 10px;
  overflow: hidden;
  background: ${lightGreyOutline};
  border-radius: 3px;
`;

const AlignmentSegment = styled.span<{
  $color: string;
  $percentage: number;
}>`
  flex: 0 0 ${({ $percentage }): number => $percentage}%;
  height: 100%;
  background: ${({ $color }): string => $color};
`;

const AxisRow = styled.div`
  display: grid;
  grid-template-columns: ${LABEL_COLUMN_WIDTH} minmax(0, 1fr);
  column-gap: 20px;
  margin-top: 8px;
  padding-right: 20px;
`;

const Axis = styled.div`
  position: relative;
  height: 24px;
  border-top: 1px solid ${lightGreyOutline};
`;

const AxisLabel = styled.span<{ $percentage: number }>`
  position: absolute;
  top: 6px;
  left: ${({ $percentage }): number => $percentage}%;
  color: ${greyText};
  font-size: 11px;
  line-height: 14px;
  transform: translateX(
    ${({ $percentage }): string => {
      if ($percentage === 0) return "0";
      if ($percentage === 100) return "-100%";
      return "-50%";
    }}
  );
`;

const clampPercentage = (value: number): number =>
  Math.min(100, Math.max(0, value * 100));

export const VotingAlignmentBars = ({
  data,
}: VotingAlignmentBarsProps): JSX.Element => {
  return (
    <ChartContainer>
      <BarsViewport data-testid="voting-alignment-scroll-region">
        {data.map((votingItem) => {
          const alignedPercentage = clampPercentage(votingItem.alignedPct);

          return (
            <AlignmentRow key={`${votingItem.managerId}-${votingItem.manager}`}>
              <ManagerLabel title={votingItem.manager}>
                {votingItem.manager}
              </ManagerLabel>
              <AlignmentTrack
                role="img"
                aria-label={`${votingItem.manager}: ${Math.round(
                  alignedPercentage,
                )}% aligned votes`}
              >
                <AlignmentSegment
                  $color={votingAlignmentBarsPalette[0]}
                  $percentage={alignedPercentage}
                />
                <AlignmentSegment
                  $color={votingAlignmentBarsPalette[1]}
                  $percentage={100 - alignedPercentage}
                />
              </AlignmentTrack>
            </AlignmentRow>
          );
        })}
      </BarsViewport>
      <AxisRow aria-label="Voting alignment percentage scale">
        <span aria-hidden="true" />
        <Axis>
          {PERCENTAGE_TICKS.map((percentage) => (
            <AxisLabel key={percentage} $percentage={percentage}>
              {percentage}%
            </AxisLabel>
          ))}
        </Axis>
      </AxisRow>
    </ChartContainer>
  );
};

type VotingAlignmentBarsProps = {
  data: VotingAlignment[];
};
